import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Button,
    Text,
    VisuallyHidden,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import StandardDrawer from '../standard-drawer'
import {CheckMarkIcon} from '../icons'

/**
 * Mobile sort sheet: a bottom sheet (StandardDrawer is `bottom` on small
 * screens) listing the sort options with a check against the selected one, and
 * the drawer's built-in X to close. Picking an option applies it and closes the
 * sheet — there is no Apply button, matching the design.
 *
 * Desktop keeps its own popover (`product-list/partials/sort.jsx`), so callers
 * only render this below the lg breakpoint.
 */
const SortSheet = ({isOpen, onClose, options = [], selected, onSelect, title}) => {
    const {formatMessage} = useIntl()
    const headingId = 'sort-sheet-heading'
    const heading = title ?? formatMessage({id: 'sort_sheet.title', defaultMessage: 'Sort'})

    return (
        <StandardDrawer
            isOpen={isOpen}
            onClose={onClose}
            ariaLabelledBy={headingId}
            closeAriaLabel={formatMessage({
                id: 'sort_sheet.close.assistive_msg',
                defaultMessage: 'Close sort options'
            })}
            data-testid="sort-sheet"
        >
            <VisuallyHidden>
                <Text as="h2" id={headingId}>
                    {heading}
                </Text>
            </VisuallyHidden>
            <Box as="ul" listStyleType="none" margin={0} padding={0}>
                {options.map((option) => {
                    const isSelected = option.value === selected

                    return (
                        <Box as="li" key={option.value}>
                            <Button
                                variant="unstyled"
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                width="100%"
                                height="auto"
                                paddingY={4}
                                gap={4}
                                textAlign="left"
                                fontWeight="normal"
                                onClick={() => {
                                    onSelect(option.value)
                                    onClose()
                                }}
                                aria-pressed={isSelected}
                                data-testid={`sort-sheet-option-${option.value}`}
                            >
                                <Text as="span">{option.label}</Text>
                                {isSelected && <CheckMarkIcon data-testid="sort-sheet-check" />}
                            </Button>
                        </Box>
                    )
                })}
            </Box>
        </StandardDrawer>
    )
}

SortSheet.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired
        })
    ),
    selected: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    title: PropTypes.string
}

export default SortSheet
